import React from 'react';
import PropTypes from 'prop-types';

import {Box, Checkbox, Circle, Flex, Text} from '@spothero/ui';

/* eslint-disable camelcase */
const STATUSES = {
    on_sales_allowed: 'ON',
    on_sales_not_allowed: 'ON',
    on_direct_link_only: 'DIRECT LINK',
    archived: 'ARCHIVED',
    off_admin_viewable: 'OFF',
    off: 'OFF',
};
/* eslint-enable camelcase */

const sharedTextStyles = {
    fontSize: 'xs',
    lineHeight: 4,
    fontWeight: 'inherit',
};

const detailsCardStyles = ({
    isSelected = false,
    isMultiSelectEnabled = false,
    showCanonicalGroups = false,
}) => ({
    paddingLeft: isMultiSelectEnabled && isSelected ? 3 : 5,
    paddingRight: 4,
    paddingY: 6,
    width: '100%',
    height: 28,
    borderLeftWidth: isSelected ? '8px' : '0px',
    borderLeftColor: 'primary.default',
    marginTop: '-1px',
    textAlign: 'left',
    outline: '1px solid',
    outlineColor: isSelected ? 'primary.500' : 'gray.200',
    outlineOffset: '-1px',
    position: 'relative',
    zIndex: isSelected ? 'layer1' : 'base',
    fontWeight: isSelected ? 'semibold' : 'normal',
    ...(!showCanonicalGroups && {
        borderTopLeftRadius: 'base',
        borderTopRightRadius: 'base',
    }),
    _last: {
        borderBottomLeftRadius: 'base',
        borderBottomRightRadius: 'base',
    },
    _focus: {
        boxShadow: '0 0 0 3px rgba(91, 170, 250, 0.7) inset',
    },
    _hover: {
        backgroundColor: 'gray.light',
    },
});

const FacilityCardDetails = ({
    facilityEntity,
    handleSelectParkingSpot,
    index,
    isMultiSelectEnabled = false,
    isSelected,
    showCanonicalGroups = false,
}) => {
    const {
        operatorFacilityId,
        id: parkingSpotId,
        status,
        title,
    } = facilityEntity;

    const shouldShowActiveIndicator =
        STATUSES[status]?.toLowerCase().includes('on') ||
        STATUSES[status]?.toLowerCase().includes('direct');

    const handleClick = (evt) => {
        evt.preventDefault();

        handleSelectParkingSpot(facilityEntity);
    };

    return (
        <Box
            key={index}
            data-testid={`FacilityCard-DetailsCard${
                isSelected ? '-Selected' : ''
            }`}
            as="button"
            onClick={handleClick}
            {...detailsCardStyles({
                isSelected,
                isMultiSelectEnabled,
                showCanonicalGroups,
            })}
        >
            <Flex alignItems="flex-start">
                {isMultiSelectEnabled && (
                    <Checkbox
                        isChecked={isSelected}
                        onChange={handleClick}
                        marginRight={2}
                        sx={{
                            '.chakra-checkbox__control': {
                                margin: 0,
                            },
                        }}
                    />
                )}
                <Flex
                    flexDirection="column"
                    justifyContent="space-between"
                    gap={2}
                    width="100%"
                    height={14}
                >
                    {title && (
                        <Text {...sharedTextStyles} height={8} noOfLines={2}>
                            {title}
                        </Text>
                    )}
                    <Flex
                        justifyContent="space-between"
                        gap={2}
                        maxWidth={isMultiSelectEnabled ? 64 : 72}
                    >
                        <Flex alignItems="center" gap={2} maxWidth={24}>
                            <Circle
                                backgroundColor={
                                    shouldShowActiveIndicator
                                        ? 'green.default'
                                        : 'red.default'
                                }
                                boxSize={2.5}
                            />
                            <Text {...sharedTextStyles}>
                                {STATUSES[status]}
                            </Text>
                        </Flex>
                        <Flex gap={2} maxWidth={48}>
                            {parkingSpotId && (
                                <Text
                                    {...sharedTextStyles}
                                    color="gray.dark"
                                    isTruncated
                                >
                                    SH ID: {parkingSpotId}
                                </Text>
                            )}
                            {operatorFacilityId && (
                                <Text
                                    {...sharedTextStyles}
                                    color="gray.dark"
                                    isTruncated
                                >
                                    ID: {operatorFacilityId}
                                </Text>
                            )}
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    );
};

FacilityCardDetails.propTypes = {
    facilityEntity: PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
        status: PropTypes.string,
        productOfferings: PropTypes.arrayOf(PropTypes.string),
        canonicalFacilityId: PropTypes.number,
        operatorFacilityId: PropTypes.string,
        canEditTransientRates: PropTypes.bool,
        canEditEventRates: PropTypes.bool,
        canEditEventTiers: PropTypes.bool,
    }),
    handleSelectParkingSpot: PropTypes.func,
    index: PropTypes.number,
    isMultiSelectEnabled: PropTypes.bool.isRequired,
    isSelected: PropTypes.bool,
    showCanonicalGroups: PropTypes.bool,
};

export default FacilityCardDetails;
